/**
 * Client-side Auth Hooks
 * React hooks for auth state management (framework-agnostic, no Next.js dependency)
 *
 * IMPORTANT: Pass your app's singleton Supabase client to useAuth() to ensure
 * session state is shared across all components. Creating multiple Supabase
 * client instances causes session isolation bugs.
 */

"use client";

import type { SupabaseClient } from "@supabase/supabase-js";
import { createBrowserClient } from "@supabase/ssr";
import { useEffect, useState, useCallback, useMemo } from "react";
import type { AuthUser, AuthState, AuthActions } from "./types";
import { AUTH_CONFIG } from "./constants";

/**
 * Resolve Supabase URL from environment.
 * Supports both Vite (import.meta.env) and process.env patterns.
 */
function getSupabaseUrl(): string {
  // Vite apps
  if (
    typeof import.meta !== "undefined" &&
    (import.meta as any).env?.VITE_SUPABASE_URL
  ) {
    return (import.meta as any).env.VITE_SUPABASE_URL;
  }
  // Fallback to process.env (build-time define or Node)
  if (typeof process !== "undefined" && process.env?.VITE_SUPABASE_URL) {
    return process.env.VITE_SUPABASE_URL;
  }
  return "https://placeholder.supabase.co";
}

/**
 * Resolve Supabase anon key from environment.
 * Supports both Vite (import.meta.env) and process.env patterns.
 */
function getSupabaseAnonKey(): string {
  // Vite apps
  if (
    typeof import.meta !== "undefined" &&
    (import.meta as any).env?.VITE_SUPABASE_ANON_KEY
  ) {
    return (import.meta as any).env.VITE_SUPABASE_ANON_KEY;
  }
  // Fallback to process.env (build-time define or Node)
  if (typeof process !== "undefined" && process.env?.VITE_SUPABASE_ANON_KEY) {
    return process.env.VITE_SUPABASE_ANON_KEY;
  }
  return "placeholder-key";
}

// Module-level singleton so createBrowserClient is only called once
let _sharedClient: SupabaseClient | null = null;

function getSharedClient(): SupabaseClient {
  if (!_sharedClient) {
    _sharedClient = createBrowserClient(
      getSupabaseUrl(),
      getSupabaseAnonKey(),
      {
        auth: {
          flowType: "pkce",
        },
      },
    );
  }
  return _sharedClient;
}

/**
 * React hook for auth state.
 *
 * @param client - Optional Supabase client instance. If provided, the hook uses
 *   this client for all auth operations, ensuring session state is shared with
 *   the rest of the app. If omitted, a module-level singleton is used.
 *
 * @returns Auth state and actions (user, session, signIn, signOut, loading)
 */
export function useAuth(client?: SupabaseClient): AuthState & AuthActions {
  const [state, setState] = useState<AuthState>({
    user: null,
    session: null,
    loading: true,
    error: null,
  });

  // Use the provided client, or fall back to the module-level singleton.
  // useMemo ensures we don't create a new reference on every render.
  const supabase = useMemo(() => client ?? getSharedClient(), [client]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (error) {
        setState({
          user: null,
          session: null,
          loading: false,
          error: error.message,
        });
        return;
      }

      if (session) {
        const authUser: AuthUser = {
          id: session.user.id,
          email: session.user.email || "",
          role:
            (session.user.user_metadata?.role as AuthUser["role"]) || "user",
          created_at: session.user.created_at || new Date().toISOString(),
        };

        setState({
          user: authUser,
          session: {
            access_token: session.access_token,
            refresh_token: session.refresh_token || "",
            expires_at: session.expires_at || 0,
            user: authUser,
          },
          loading: false,
          error: null,
        });
      } else {
        setState({
          user: null,
          session: null,
          loading: false,
          error: null,
        });
      }
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        const authUser: AuthUser = {
          id: session.user.id,
          email: session.user.email || "",
          role:
            (session.user.user_metadata?.role as AuthUser["role"]) || "user",
          created_at: session.user.created_at || new Date().toISOString(),
        };

        setState({
          user: authUser,
          session: {
            access_token: session.access_token,
            refresh_token: session.refresh_token || "",
            expires_at: session.expires_at || 0,
            user: authUser,
          },
          loading: false,
          error: null,
        });
      } else {
        setState({
          user: null,
          session: null,
          loading: false,
          error: null,
        });
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [supabase]);

  const signInWithEmail = useCallback(
    async (email: string): Promise<{ success: boolean; message: string }> => {
      const { error } = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}${AUTH_CONFIG.AUTH_CALLBACK_PATH}`,
        },
      });

      if (error) {
        return {
          success: false,
          message: error.message,
        };
      }

      return {
        success: true,
        message: `Magic link sent to ${email}. Check your inbox.`,
      };
    },
    [supabase],
  );

  const signInWithOAuth = useCallback(
    async (provider: "google" | "github"): Promise<{ url: string }> => {
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}${AUTH_CONFIG.AUTH_CALLBACK_PATH}`,
        },
      });

      if (error || !data.url) {
        throw new Error(`Failed to get ${provider} auth URL`);
      }

      return { url: data.url };
    },
    [supabase],
  );

  const signUp = useCallback(
    async (
      email: string,
      password: string,
      fullName?: string,
    ): Promise<{ success: boolean; message: string }> => {
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}${AUTH_CONFIG.AUTH_CALLBACK_PATH}`,
          data: fullName ? { full_name: fullName } : undefined,
        },
      });

      if (error) {
        return {
          success: false,
          message: error.message,
        };
      }

      return {
        success: true,
        message:
          "Account created successfully. Please check your email to verify your account.",
      };
    },
    [supabase],
  );

  const resetPassword = useCallback(
    async (email: string): Promise<{ success: boolean; message: string }> => {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${AUTH_CONFIG.AUTH_CALLBACK_PATH}`,
      });

      if (error) {
        return {
          success: false,
          message: error.message,
        };
      }

      return {
        success: true,
        message: `Password reset instructions sent to ${email}`,
      };
    },
    [supabase],
  );

  const signOut = useCallback(async (): Promise<void> => {
    await supabase.auth.signOut();
  }, [supabase]);

  const refreshSession = useCallback(async (): Promise<boolean> => {
    const { data, error } = await supabase.auth.refreshSession();
    return !error && !!data.session;
  }, [supabase]);

  return {
    ...state,
    signInWithEmail,
    signInWithOAuth,
    signUp,
    resetPassword,
    signOut,
    refreshSession,
  };
}

/**
 * React hook that redirects to login if not authenticated.
 * Uses window.location for framework-agnostic navigation.
 */
export function useRequireAuth(): AuthState & AuthActions {
  const auth = useAuth();

  useEffect(() => {
    if (!auth.loading && !auth.user) {
      window.location.href = AUTH_CONFIG.LOGIN_PATH;
    }
  }, [auth.loading, auth.user]);

  return auth;
}
